/**
 * 알림 신청 추적 훅 (종목/전략/리포트)
 * 
 * 종목, 전략, 리포트 알림을 신청하는 기능
 * - start()는 세션 스토리지를 확인하여 저장된 의도가 있으면 자동 복원
 * - 로그인 후 리다이렉트되어도 같은 의도 유지
 * - 로그인 의도는 자동으로 부모 의도 연결 (parentIntentType)
 * 
 * @example
 * ```tsx
 * // 종목 알림
 * function StockAlarmButton({ ticker, market }) {
 *   const stockAlarm = useStockAlarm();
 *   const loginIntent = useLoginIntent();
 *   const isLoggedIn = useAuth().isLoggedIn;
 *   
 *   const handleAlarm = async () => {
 *     stockAlarm.start({
 *       ticker,
 *       market,
 *       from_section_id: 'stock_detail_header'
 *     });
 *     
 *     if (!isLoggedIn) {
 *       loginIntent.start(
 *         { redirectUrl: router.asPath },
 *         { parentIntentType: 'stock_alarm' }
 *       );
 *       router.push('/login');
 *       return;
 *     }
 *     
 *     try {
 *       await apiSetStockAlarm(ticker, market);
 *       stockAlarm.success();
 *     } catch (error) {
 *       stockAlarm.fail('backend_5xx');
 *     }
 *   };
 * }
 * 
 * // 전략 알림
 * function StrategyAlarmButton({ strategyId, market }) {
 *   const strategyAlarm = useStrategyAlarm();
 *   
 *   const handleAlarm = async () => {
 *     strategyAlarm.start({
 *       strategy_id: strategyId,
 *       market,
 *       from_section_id: 'strategy_detail_header'
 *     });
 *     // ... (로그인 체크 로직)
 *   };
 * }
 * 
 * // 리포트 알림
 * function ReportAlarmButton({ reportId }) {
 *   const reportAlarm = useReportAlarm();
 *   
 *   const handleAlarm = async () => {
 *     reportAlarm.start({
 *       report_id: reportId,
 *       from_list_id: 'report_detail_page'
 *     });
 *     // ... (로그인 체크 로직)
 *   };
 * }
 * ```
 */

import { useCallback } from 'react';
import { track } from '../../../core/client';
import { useIntent } from './useIntent';

// === 종목 알림 ===

export interface StockAlarmContext {
    ticker: string;
    market: 'kr' | 'us';
    from_section_id: string;
}

export interface StockAlarmActions {
    start: (context: StockAlarmContext) => void;
    success: () => void;
    fail: (reason?: string) => void;
}

/**
 * 종목 알림 신청 추적 훅
 */
export function useStockAlarm(): StockAlarmActions {
    const intent = useIntent<StockAlarmContext>({
        type: 'stock_alarm',
        requiredGate: 'login',
        onStart: ({ intent_id, context, parent_intent_id, required_gate }) => {
            if (!context) {
                console.warn('⚠️ [StockAlarm] start() requires context');
                return;
            }
            track('stock_alarm_start', {
                intent_id,
                ticker: context.ticker,
                market: context.market,
                from_section_id: context.from_section_id,
                parent_intent_id,
                required_gate,
            });
        },
        onSuccess: ({ intent_id, context, latency_ms }) => {
            if (!context) return;
            track('stock_alarm_success', {
                intent_id,
                ticker: context.ticker,
                market: context.market,
                from_section_id: context.from_section_id,
                latency_ms,
            });
        },
        onFail: ({ intent_id, context, fail_reason }) => {
            if (!context) return;
            track('stock_alarm_fail', {
                intent_id,
                ticker: context.ticker,
                market: context.market,
                from_section_id: context.from_section_id,
                fail_reason,
            });
        },
    });

    const start = useCallback((context: StockAlarmContext) => {
        intent.start(context);
    }, [intent]);

    const success = useCallback(() => {
        intent.success();
    }, [intent]);

    const fail = useCallback((reason?: string) => {
        intent.fail(reason);
    }, [intent]);

    return {
        start,
        success,
        fail,
    };
}

// === 전략 알림 ===

export interface StrategyAlarmContext {
    strategy_id: string;
    market: 'kr' | 'us';
    from_section_id: string;
}

export interface StrategyAlarmActions {
    start: (context: StrategyAlarmContext) => void;
    success: () => void;
    fail: (reason?: string) => void;
}

/**
 * 전략 알림 신청 추적 훅
 */
export function useStrategyAlarm(): StrategyAlarmActions {
    const intent = useIntent<StrategyAlarmContext>({
        type: 'strategy_alarm',
        requiredGate: 'login',
        onStart: ({ intent_id, context, parent_intent_id, required_gate }) => {
            if (!context) {
                console.warn('⚠️ [StrategyAlarm] start() requires context');
                return;
            }
            track('strategy_alarm_start', {
                intent_id,
                strategy_id: context.strategy_id,
                market: context.market,
                from_section_id: context.from_section_id,
                parent_intent_id,
                required_gate,
            });
        },
        onSuccess: ({ intent_id, context, latency_ms }) => {
            if (!context) return;
            track('strategy_alarm_success', {
                intent_id,
                strategy_id: context.strategy_id,
                market: context.market,
                from_section_id: context.from_section_id,
                latency_ms,
            });
        },
        onFail: ({ intent_id, context, fail_reason }) => {
            if (!context) return;
            track('strategy_alarm_fail', {
                intent_id,
                strategy_id: context.strategy_id,
                market: context.market,
                from_section_id: context.from_section_id,
                fail_reason,
            });
        },
    });

    const start = useCallback((context: StrategyAlarmContext) => {
        intent.start(context);
    }, [intent]);

    const success = useCallback(() => {
        intent.success();
    }, [intent]);

    const fail = useCallback((reason?: string) => {
        intent.fail(reason);
    }, [intent]);

    return {
        start,
        success,
        fail,
    };
}

// === 리포트 알림 ===

export interface ReportAlarmContext {
    report_id?: number;
    from_list_id: string;
}

export interface ReportAlarmActions {
    start: (context: ReportAlarmContext) => void;
    success: () => void;
    fail: (reason?: string) => void;
}

/**
 * 리포트 알림 신청 추적 훅
 */
export function useReportAlarm(): ReportAlarmActions {
    const intent = useIntent<ReportAlarmContext>({
        type: 'report_alarm',
        requiredGate: 'login',
        onStart: ({ intent_id, context, parent_intent_id, required_gate }) => {
            if (!context) {
                console.warn('⚠️ [ReportAlarm] start() requires context');
                return;
            }
            track('report_alarm_start', {
                intent_id,
                report_id: context?.report_id || undefined,
                from_list_id: context.from_list_id,
                parent_intent_id,
                required_gate,
            });
        },
        onSuccess: ({ intent_id, context, latency_ms }) => {
            if (!context) return;
            track('report_alarm_success', {
                intent_id,
                report_id: context?.report_id || undefined,
                from_list_id: context.from_list_id,
                latency_ms,
            });
        },
        onFail: ({ intent_id, context, fail_reason }) => {
            if (!context) return;
            track('report_alarm_fail', {
                intent_id,
                report_id: context?.report_id || undefined,
                from_list_id: context.from_list_id,
                fail_reason,
            });
        },
    });

    const start = useCallback((context: ReportAlarmContext) => {
        intent.start(context);
    }, [intent]);

    const success = useCallback(() => {
        intent.success();
    }, [intent]);

    const fail = useCallback((reason?: string) => {
        intent.fail(reason);
    }, [intent]);

    return {
        start,
        success,
        fail,
    };
}

